import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Role } from '@core/models/role.model';
import { AuthService } from '@core/services/auth.service';
import { TokenService } from '@core/services/token/token.service';
import { I18nService } from '@yourcause/common/i18n';
import { NotifierService } from '@yourcause/common/notifier';

/**
 * Route guard to restrict access based on user roles.
 *   examples:
 *    canActivate: [inAnyRole([Role.NonprofitAdmin, Role.NonprofitManager])]
 *      - NonprofitAdmin or NonprofitManager only
 *    canActivate: [inAnyRole([Role.NonprofitAdmin, Role.NonprofitManager], false)],
 *      - Everyone but NonprofitAdmin or NonprofitManager
 * @param allowedRoles Array of roles that are allowed to access the route.
 * @param isInRole Whether the user is in the allowed roles (default true)
 * @returns A function that checks if the user has the required roles.
 */
export const inAnyRole = (allowedRoles: Role[], isInRole: boolean = true): CanActivateFn => {
  return async (route, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const tokenService = inject(TokenService);
    const notifier = inject(NotifierService);
    const i18n = inject(I18nService);

    try {
      // user is not logged in redirect to login
      if (!tokenService.getIsLoggedIn()) {
        return router.createUrlTree(['login'], { queryParams: { returnUrl: state.url } });
      }

      if (!tokenService.tokenInfo) {
        await tokenService.fetchUserClaims();
      }

      const ok = authService.hasAnyRole(allowedRoles) === isInRole;
      if (!ok) {
        return router.parseUrl('/forbidden');
      }

      return true;
    } catch (err) {
      notifier.error(i18n.translate('error:textGeneric'));

      return router.parseUrl('/forbidden');
    }
  };
};
